import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { InvoicePreview, type PreviewLine } from '@/client/components/invoices/InvoicePreview';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Checkbox } from '@/client/components/ui/checkbox';
import { DatePicker } from '@/client/components/ui/date-picker';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { Textarea } from '@/client/components/ui/textarea';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { type Currency, currencySymbol, fmtAmount } from '@/client/lib/currency';
import { toISODate } from '@/client/lib/dates';
import { trpc } from '@/client/utils/trpc';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Check, Download, Mail, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/invoices/new')({
  component: InvoiceNewPage,
});

type LineItem = {
  id: string;
  timesheet_entry_id: string | null;
  person_id: string;
  person_name: string;
  date: string;
  description: string;
  hours: number;
  sort_order: number;
  included: boolean;
  is_shadow: boolean;
  is_manual: boolean;
};

function getLast13Months(): { value: string; label: string }[] {
  const now = new Date();
  return Array.from({ length: 13 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const value = `${year}-${String(month).padStart(2, '0')}`;
    const lastDay = new Date(year, month, 0).getDate();
    const start = `${String(1).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
    const end = `${String(lastDay).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
    const label = `${d.toLocaleString('en-US', { month: 'long' })} ${year} (${start}–${end})`;
    return { value, label };
  });
}

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toISODate(d); // local date; toISOString() would shift a day east of UTC
}

function SectionHeader({ n, title, sub }: { n: string; title: string; sub?: string }) {
  return (
    <div className="px-5 py-4 border-b">
      <h3 className="text-sm font-semibold">
        {n} · {title}
      </h3>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function InvoiceNewPage() {
  useAdminGuard();
  const navigate = useNavigate();

  const today = new Date().toISOString().slice(0, 10);
  const months = getLast13Months();
  const [billingPeriod, setBillingPeriod] = useState(months[0].value);

  const [selectedClientName, setSelectedClientName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate, setDueDate] = useState(addDays(today, 30));
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [lateFee, setLateFee] = useState('3% / month');
  const [vatPct, setVatPct] = useState(20);
  const [showNames, setShowNames] = useState(true);
  const [groupByPerson, setGroupByPerson] = useState(false);
  const [lines, setLines] = useState<LineItem[]>([]);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [currency, setCurrency] = useState<Currency>('EUR');
  const [error, setError] = useState<string | null>(null);

  const [bpYear, bpMonth] = billingPeriod.split('-');
  const billingYear = Number.parseInt(bpYear, 10);
  const billingMonth = Number.parseInt(bpMonth, 10);

  const billingStart = `${billingYear}-${String(billingMonth).padStart(2, '0')}-01`;
  const billingEnd = (() => {
    const lastDay = new Date(Date.UTC(billingYear, billingMonth, 0)).getUTCDate();
    return `${billingYear}-${String(billingMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  })();

  const { data: projects = [] } = trpc.projects.list.useQuery();
  const { data: employees = [] } = trpc.employees.list.useQuery();
  const { data: nextNumber } = trpc.invoices.nextNumber.useQuery();
  const { data: tsLines } = trpc.invoices.timesheetLines.useQuery(
    { project_id: projectId, year: billingYear, month: billingMonth },
    { enabled: !!projectId && !Number.isNaN(billingYear) && !Number.isNaN(billingMonth) },
  );

  const utils = trpc.useUtils();
  const createMutation = trpc.invoices.create.useMutation({
    onSuccess: () => {
      utils.invoices.list.invalidate();
      utils.invoices.nextNumber.invalidate();
      navigate({ to: '/invoices', search: { status: 'ALL' } });
    },
    onError: (err) => setError(err.message),
  });

  useEffect(() => {
    if (nextNumber && !invoiceNumber) setInvoiceNumber(nextNumber);
  }, [nextNumber, invoiceNumber]);

  useEffect(() => {
    if (!tsLines) return;
    setLines(
      tsLines.map((e) => ({
        id: e.id,
        timesheet_entry_id: e.is_shadow ? null : e.id,
        person_id: e.person_id,
        person_name: e.person_name,
        date: e.date,
        description: e.description,
        hours: e.hours,
        sort_order: e.sort_order,
        included: true,
        is_shadow: e.is_shadow,
        is_manual: false,
      })),
    );
    setRates((prev) => {
      const next = { ...prev };
      for (const e of tsLines) {
        if (!(e.person_name in next)) next[e.person_name] = e.default_rate;
      }
      return next;
    });
  }, [tsLines]);

  const clientNames = useMemo(
    () =>
      Array.from(
        new Set(projects.map((p) => p.client_name).filter((c): c is string => Boolean(c))),
      ).sort(),
    [projects],
  );

  const filteredProjects = useMemo(
    () =>
      selectedClientName ? projects.filter((p) => p.client_name === selectedClientName) : projects,
    [projects, selectedClientName],
  );

  const selectedProject = projects.find((p) => p.id === projectId);

  const people = useMemo(() => {
    const names = Array.from(new Set(lines.map((l) => l.person_name))).filter(Boolean);
    return names.map((name) => {
      const personLines = lines.filter((l) => l.person_name === name);
      const includedLines = personLines.filter((l) => l.included);
      const hours = includedLines.reduce((s, l) => s + l.hours, 0);
      const amount = hours * (rates[name] ?? 0);
      return {
        name,
        hours,
        amount,
        lineCount: personLines.length,
        includedCount: includedLines.length,
        isShadow: personLines.some((l) => l.is_shadow),
      };
    });
  }, [lines, rates]);

  const subtotal = useMemo(
    () =>
      lines
        .filter((l) => l.included)
        .reduce((s, l) => s + l.hours * (rates[l.person_name] ?? 0), 0),
    [lines, rates],
  );
  const vatAmount = (subtotal * vatPct) / 100;
  const total = subtotal + vatAmount;

  const includedLines = useMemo(() => lines.filter((l) => l.included), [lines]);
  const totalHours = useMemo(() => includedLines.reduce((s, l) => s + l.hours, 0), [includedLines]);
  const allIncluded = useMemo(() => lines.length > 0 && lines.every((l) => l.included), [lines]);
  const someIncluded = useMemo(() => lines.some((l) => l.included), [lines]);

  function setLine(id: string, patch: Partial<LineItem>) {
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }

  function toggleAll() {
    const next = !someIncluded;
    setLines((ls) => ls.map((l) => ({ ...l, included: next })));
  }

  function addManualLine() {
    setGroupByPerson(false);
    setLines((ls) => [
      ...ls,
      {
        id: crypto.randomUUID(),
        timesheet_entry_id: null,
        person_id: '',
        person_name: '',
        date: today,
        description: '',
        hours: 0,
        sort_order: ls.length,
        included: true,
        is_shadow: false,
        is_manual: true,
      },
    ]);
  }

  function handleSave(status: 'DRAFT' | 'SENT') {
    if (!invoiceNumber.trim()) {
      setError('Invoice number is required.');
      return;
    }
    if (!selectedProject && !selectedClientName) {
      setError('Select a client.');
      return;
    }
    if (includedLines.some((l) => !l.person_name.trim())) {
      setError('Pick a person for every manual line, or remove it.');
      return;
    }
    setError(null);
    const clientName = selectedProject?.client_name ?? selectedClientName ?? '';
    createMutation.mutate({
      number: invoiceNumber.trim(),
      project_id: projectId || null,
      client_name: clientName,
      billing_period_start: billingStart,
      billing_period_end: billingEnd,
      issue_date: issueDate,
      due_date: dueDate,
      status,
      notes: notes.trim() || undefined,
      payment_terms: paymentTerms || undefined,
      late_fee: lateFee || undefined,
      vat_pct: vatPct,
      currency,
      subtotal,
      vat_amount: vatAmount,
      total,
      lines: includedLines.map((l, i) => ({
        timesheet_entry_id: l.timesheet_entry_id,
        person_name: l.person_name,
        date: l.date,
        description: l.description,
        hours: l.hours,
        rate: rates[l.person_name] ?? 0,
        amount: l.hours * (rates[l.person_name] ?? 0),
        sort_order: i,
      })),
    });
  }

  const previewLines = useMemo<PreviewLine[]>(
    () =>
      includedLines.map((l) => ({
        id: l.id,
        person_name: l.person_name,
        date: l.date,
        description: l.description,
        hours: l.hours,
        rate: rates[l.person_name] ?? 0,
      })),
    [includedLines, rates],
  );

  return (
    <div className="-mx-6 -mt-6 -mb-6 flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b px-6 py-3 bg-background shrink-0">
        <div>
          <p className="text-xs text-muted-foreground">Invoices</p>
          <h1 className="text-lg font-bold tracking-tight leading-tight">New invoice</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/invoices', search: { status: 'ALL' } })}
          >
            <X className="mr-1.5 h-4 w-4" />
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave('DRAFT')}
            disabled={createMutation.isPending}
          >
            Save draft
          </Button>
          <Button size="sm" onClick={() => handleSave('SENT')} disabled={createMutation.isPending}>
            <Check className="mr-1.5 h-4 w-4" />
            Issue invoice
          </Button>
        </div>
      </div>

      {/* 2-column body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: composer */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 min-w-0">
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md px-4 py-2">
              {error}
            </p>
          )}

          {/* 1. Invoice details */}
          <Card className="overflow-hidden p-0">
            <SectionHeader n="1" title="Invoice details" sub="Who is this for and when." />
            <CardContent className="p-5 grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select
                  value={selectedClientName}
                  onValueChange={(v) => {
                    setSelectedClientName(v);
                    setProjectId('');
                    setLines([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client…" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientNames.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Project</Label>
                <Select
                  value={projectId}
                  onValueChange={(v) => {
                    setProjectId(v);
                    setLines([]);
                    const proj = projects.find((p) => p.id === v);
                    if (proj?.client_name) setSelectedClientName(proj.client_name);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project…" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Billing period</Label>
                <Select
                  value={billingPeriod}
                  onValueChange={(v) => {
                    setBillingPeriod(v);
                    setLines([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Invoice #</Label>
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Issue date</Label>
                <DatePicker
                  value={issueDate}
                  onChange={(date) => {
                    setIssueDate(date);
                    setDueDate(addDays(date, 30));
                  }}
                  required
                  aria-label="Issue date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  Due date{' '}
                  <span className="font-normal text-muted-foreground text-xs">
                    (Net 30 by default)
                  </span>
                </Label>
                <DatePicker
                  value={dueDate}
                  onChange={setDueDate}
                  min={issueDate || undefined}
                  required
                  presets={[14, 30, 45, 60].map((n) => ({
                    label: `Net ${n}`,
                    value: addDays(issueDate, n),
                  }))}
                  aria-label="Due date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={(v) => setCurrency(v as Currency)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR — Euro (€)</SelectItem>
                    <SelectItem value="USD">USD — US Dollar ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* 2. People & rates */}
          <Card className="overflow-hidden p-0">
            <div className="px-5 py-4 border-b flex justify-between items-center">
              <div>
                <h3 className="text-sm font-semibold">2 · People &amp; rates</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Hourly rates loaded from profile; edit to override for this invoice.
                </p>
              </div>
            </div>
            {people.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {projectId
                  ? 'No timesheet entries found for this period.'
                  : 'Select a project and billing period to load entries.'}
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-[1fr_100px_100px_120px] gap-3 px-5 py-2.5 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <div>Person</div>
                  <div className="text-right">Hours</div>
                  <div className="text-right">Rate / h</div>
                  <div className="text-right">Subtotal</div>
                </div>
                {people.map((p) => (
                  <div
                    key={p.name}
                    className="grid grid-cols-[1fr_100px_100px_120px] gap-3 px-5 py-3.5 border-b last:border-0 items-center"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <InitialsAvatar name={p.name} size={32} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium">{p.name}</div>
                          {p.isShadow && (
                            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
                              Shadow
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.includedCount} of {p.lineCount} entries included
                        </div>
                      </div>
                    </div>
                    <div className="text-right tabular-nums text-sm font-medium">
                      {p.hours.toFixed(1)}h
                    </div>
                    <div className="flex justify-end">
                      <div className="relative w-24">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {currencySymbol(currency)}
                        </span>
                        <Input
                          className="pl-6 pr-1 text-right text-sm tabular-nums h-8"
                          value={String(rates[p.name] ?? 0)}
                          onChange={(e) =>
                            setRates((r) => ({
                              ...r,
                              [p.name]: Number.parseFloat(e.target.value) || 0,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="text-right tabular-nums text-sm font-semibold">
                      {fmtAmount(p.amount, currency)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 3. Line items */}
          <Card className="overflow-hidden p-0">
            <div className="px-5 py-4 border-b flex justify-between items-center gap-3">
              <div>
                <h3 className="text-sm font-semibold">
                  3 · Line items{' '}
                  <span className="text-muted-foreground font-normal">from timesheet</span>
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Toggle entries in or out, edit hours or descriptions inline.
                </p>
              </div>
              <div className="flex rounded-md border overflow-hidden shrink-0">
                {(['Detailed', 'Group by person'] as const).map((label, i) => {
                  const active = i === 0 ? !groupByPerson : groupByPerson;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setGroupByPerson(i === 1)}
                      className={[
                        'px-3 py-1 text-xs font-medium transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      ].join(' ')}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-3 px-5 py-2.5 border-b bg-muted/40">
              <Checkbox
                checked={allIncluded}
                onCheckedChange={toggleAll}
                aria-label="Toggle all"
                className={!allIncluded && someIncluded ? 'opacity-50' : ''}
              />
              <span className="text-xs text-muted-foreground">
                {includedLines.length} of {lines.length} entries · {totalHours.toFixed(1)}h
              </span>
              <div className="flex-1" />
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showNames}
                  onChange={(e) => setShowNames(e.target.checked)}
                  className="h-3.5 w-3.5 rounded"
                />
                Show person on invoice
              </label>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={addManualLine}
              >
                <Plus className="h-3 w-3" />
                Add manual line
              </Button>
            </div>

            {/* Lines */}
            {lines.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {projectId ? 'No entries for this period.' : 'Select a project and period above.'}
              </div>
            ) : !groupByPerson ? (
              <div>
                <div className="grid grid-cols-[32px_64px_140px_1fr_72px_80px_100px] gap-3 px-5 py-2 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <div />
                  <div>Date</div>
                  <div>Person</div>
                  <div>Description</div>
                  <div className="text-right">Hours</div>
                  <div className="text-right">Rate</div>
                  <div className="text-right">Amount</div>
                </div>
                {lines.map((l, i) => {
                  const rate = rates[l.person_name] ?? 0;
                  const amount = l.hours * rate;
                  return (
                    <div
                      key={l.id}
                      className={[
                        'grid grid-cols-[32px_64px_140px_1fr_72px_80px_100px] gap-3 px-5 py-3 items-center',
                        i < lines.length - 1 ? 'border-b' : '',
                        !l.included ? 'opacity-45' : '',
                      ].join(' ')}
                    >
                      <Checkbox
                        checked={l.included}
                        onCheckedChange={() => setLine(l.id, { included: !l.included })}
                      />
                      {l.is_manual ? (
                        <DatePicker
                          variant="ghost"
                          size="sm"
                          hideIcon
                          placeholder="Date"
                          value={l.date}
                          onChange={(date) => setLine(l.id, { date })}
                          formatValue={(iso) => iso.slice(5).replace('-', '/')}
                          aria-label="Line date"
                        />
                      ) : (
                        <div className="tabular-nums text-xs text-muted-foreground">
                          {l.date.slice(5).replace('-', '/')}
                        </div>
                      )}
                      {l.is_manual && !l.person_name ? (
                        <Select
                          value={l.person_id}
                          onValueChange={(v) => {
                            const emp = employees.find((e) => e.id === v);
                            if (!emp?.full_name) return;
                            setLine(l.id, { person_id: emp.id, person_name: emp.full_name });
                            setRates((r) =>
                              emp.full_name && !(emp.full_name in r)
                                ? { ...r, [emp.full_name]: emp.rate ?? 0 }
                                : r,
                            );
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Select person…" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <InitialsAvatar name={l.person_name} size={22} />
                          <span className="text-xs font-medium truncate">
                            {l.person_name.split(' ')[0]}
                          </span>
                        </div>
                      )}
                      <input
                        value={l.description}
                        onChange={(e) => setLine(l.id, { description: e.target.value })}
                        className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs outline-none w-full transition-colors"
                      />
                      <input
                        defaultValue={l.hours.toFixed(1)}
                        key={`${l.id}-hours`}
                        onBlur={(e) =>
                          setLine(l.id, { hours: Number.parseFloat(e.target.value) || 0 })
                        }
                        className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs tabular-nums text-right outline-none w-full transition-colors"
                      />
                      <div className="text-right tabular-nums text-xs text-muted-foreground">
                        {currencySymbol(currency)}
                        {rate}
                      </div>
                      <div className="text-right tabular-nums text-xs font-semibold">
                        {l.is_manual && !l.person_name ? (
                          <button
                            type="button"
                            onClick={() => setLines((ls) => ls.filter((x) => x.id !== l.id))}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label="Remove line"
                          >
                            <X className="h-3.5 w-3.5 ml-auto" />
                          </button>
                        ) : (
                          fmtAmount(amount, currency)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                {people.map((p, pi) => {
                  const pLines = lines.filter((l) => l.person_name === p.name);
                  return (
                    <div key={p.name} className={pi < people.length - 1 ? 'border-b' : ''}>
                      <div className="flex items-center gap-3 px-5 py-3 bg-muted/40">
                        <InitialsAvatar name={p.name} size={28} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{p.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.hours.toFixed(1)}h @ {currencySymbol(currency)}
                            {rates[p.name] ?? 0}/h
                          </div>
                        </div>
                        <div className="tabular-nums text-sm font-semibold">
                          {fmtAmount(p.amount, currency)}
                        </div>
                      </div>
                      {pLines.map((l) => (
                        <div
                          key={l.id}
                          className={[
                            'grid grid-cols-[32px_64px_1fr_72px_100px] gap-3 px-5 py-2.5 items-center border-t',
                            !l.included ? 'opacity-45' : '',
                          ].join(' ')}
                        >
                          <Checkbox
                            checked={l.included}
                            onCheckedChange={() => setLine(l.id, { included: !l.included })}
                          />
                          <div className="tabular-nums text-xs text-muted-foreground">
                            {l.date.slice(5).replace('-', '/')}
                          </div>
                          <input
                            value={l.description}
                            onChange={(e) => setLine(l.id, { description: e.target.value })}
                            className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs outline-none w-full transition-colors"
                          />
                          <input
                            defaultValue={l.hours.toFixed(1)}
                            key={`${l.id}-hours`}
                            onBlur={(e) =>
                              setLine(l.id, { hours: Number.parseFloat(e.target.value) || 0 })
                            }
                            className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs tabular-nums text-right outline-none w-full transition-colors"
                          />
                          <div className="text-right tabular-nums text-xs font-semibold">
                            {fmtAmount(l.hours * (rates[l.person_name] ?? 0), currency)}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* 4. Notes & totals */}
          <div className="grid grid-cols-[1fr_300px] gap-5">
            <Card className="overflow-hidden p-0">
              <SectionHeader n="4" title="Notes &amp; payment terms" />
              <CardContent className="p-5 flex flex-col gap-4">
                <div className="space-y-1.5">
                  <Label>Note on invoice</Label>
                  <Textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Thank you for your business. Bank transfer to…"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Payment terms</Label>
                    <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on receipt'].map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Late fee</Label>
                    <Input
                      value={lateFee}
                      onChange={(e) => setLateFee(e.target.value)}
                      placeholder="e.g. 3% / month"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                Totals
              </p>
              <div className="flex flex-col gap-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total hours</span>
                  <span className="tabular-nums font-medium">{totalHours.toFixed(1)} h</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums font-medium">{fmtAmount(subtotal, currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">VAT</span>
                  <div className="flex items-center gap-2">
                    <div className="relative w-16">
                      <Input
                        className="pr-5 text-right text-sm tabular-nums h-7"
                        defaultValue={String(vatPct)}
                        key={`${vatPct}-vat`}
                        onBlur={(e) => setVatPct(Number.parseFloat(e.target.value) || 0)}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        %
                      </span>
                    </div>
                    <span className="tabular-nums text-sm text-muted-foreground w-20 text-right">
                      {fmtAmount(vatAmount, currency)}
                    </span>
                  </div>
                </div>
                <div className="h-px bg-border my-1" />
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-semibold">Total due</span>
                  <span className="tabular-nums text-2xl font-bold">
                    {fmtAmount(total, currency)}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          <div className="pb-10" />
        </div>

        {/* Right: live preview */}
        <div className="w-[460px] border-l bg-muted/30 overflow-y-auto p-6 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Live preview
            </p>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled
                title="Download PDF (coming soon)"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled
                title="Send by email (coming soon)"
              >
                <Mail className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <InvoicePreview
            invoiceNumber={invoiceNumber}
            clientName={selectedProject?.client_name ?? selectedClientName}
            projectName={selectedProject?.name ?? ''}
            billingPeriodStart={billingStart}
            billingPeriodEnd={billingEnd}
            issueDate={issueDate}
            dueDate={dueDate}
            lines={previewLines}
            rates={rates}
            showNames={showNames}
            vatPct={vatPct}
            currency={currency}
            subtotal={subtotal}
            vatAmount={vatAmount}
            total={total}
            notes={notes}
            paymentTerms={paymentTerms}
            lateFee={lateFee}
          />
        </div>
      </div>
    </div>
  );
}
